import { useEffect, useState, type FormEvent } from 'react'
import { Link, useParams } from 'react-router-dom'
import PublicShell from '../components/PublicShell'
import Spinner from '../components/Spinner'
import { coursePath, lessonHtml, lessonPath, videoEmbedUrl } from '../components/courses/courseUtils'
import QuizPanel from '../components/courses/QuizPanel'
import { lessonsInOrder, useCourse } from '../components/courses/useCourse'
import { coursesAPI } from '../services/api'
import type { Lesson, LessonQuestion } from '../types'
import { apiErrorMessage } from '../utils/apiError'
import { COURSE_DISCLAIMER } from './CoursePage'

/** Lesson Markdown's look (there's no typography plugin): paragraphs, lists, headings, links, code. */
const LESSON_TEXT =
  'max-w-none space-y-3 leading-relaxed text-gray-200 [&_a]:text-sky-300 [&_a]:underline [&_blockquote]:border-l-2 ' +
  '[&_blockquote]:border-terminal-border [&_blockquote]:pl-3 [&_code]:rounded [&_code]:bg-white/10 [&_code]:px-1 ' +
  '[&_h1]:text-2xl [&_h1]:font-bold [&_h2]:mt-5 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:mt-4 [&_h3]:font-semibold ' +
  '[&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_img]:rounded-lg'

/**
 * The tutor for one lesson: ask, and Claude answers from the lesson itself.
 * For students (and the instructor); the questions asked so far are listed.
 */
export const LessonTutor = ({ lesson }: { lesson: Lesson }) => {
  const [asked, setAsked] = useState<LessonQuestion[]>([])
  const [question, setQuestion] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let stopped = false
    coursesAPI
      .myQuestions(lesson.id)
      .then((list) => !stopped && setAsked(list))
      .catch(() => undefined)
    return () => {
      stopped = true
    }
  }, [lesson.id])

  const ask = async (e: FormEvent) => {
    e.preventDefault()
    if (!question.trim()) return
    setBusy(true)
    setError(null)
    try {
      const answer = await coursesAPI.ask(lesson.id, question.trim())
      setAsked((list) => [answer, ...list])
      setQuestion('')
    } catch (err) {
      setError(apiErrorMessage(err, "The tutor couldn't answer. Try again in a moment."))
    } finally {
      setBusy(false)
    }
  }

  return (
    <section
      aria-labelledby="lesson-tutor"
      className="rounded-xl border border-terminal-border bg-terminal-surface p-4"
      data-testid="lesson-tutor"
    >
      <h2 id="lesson-tutor" className="font-semibold text-gray-100">
        Ask about this lesson
      </h2>
      <p className="mt-1 text-xs text-gray-400">
        Answered by AI from this lesson only. It explains; it never tells you what to buy or sell.
      </p>
      <form onSubmit={ask} className="mt-3 flex flex-wrap gap-2">
        <label htmlFor="tutor-question" className="sr-only">
          Your question
        </label>
        <input
          id="tutor-question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          maxLength={1000}
          placeholder="e.g. Why does the stop come before the size?"
          className="min-w-0 flex-1 rounded-md border border-terminal-border bg-terminal-bg px-3 py-1.5 text-sm text-gray-100"
        />
        <button
          type="submit"
          disabled={busy || !question.trim()}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
        >
          {busy ? 'Thinking…' : 'Ask'}
        </button>
      </form>
      {error && (
        <p role="alert" className="mt-2 text-sm text-amber-300">
          {error}
        </p>
      )}
      {asked.length > 0 && (
        <ul className="mt-4 space-y-3" data-testid="tutor-history">
          {asked.map((q) => (
            <li key={q.id} className="text-sm">
              <p className="font-medium text-gray-100">{q.question}</p>
              <p className="mt-1 whitespace-pre-line text-gray-300">{q.answer}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * One lesson (/learn/<slug>/lessons/<id>): its video and text, "Mark as
 * done", the tutor, and the way to the next lesson. Anyone may read a free
 * preview; the rest is for students, and the page says so.
 */
const LessonPage = () => {
  const { slug, lessonId } = useParams()
  const { state, setState } = useCourse(slug)
  const [marking, setMarking] = useState(false)
  const [markError, setMarkError] = useState<string | null>(null)

  if (state.kind !== 'ready') {
    return (
      <PublicShell>
        {state.kind === 'loading' ? (
          <div className="flex justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <div className="py-16 text-center" data-testid="lesson-missing">
            <h1 className="text-xl font-semibold text-gray-100">This lesson could not be found</h1>
          </div>
        )}
      </PublicShell>
    )
  }

  const { course, modules, enrollment, completed, manages } = state
  const lessons = lessonsInOrder(modules)
  const index = lessons.findIndex((l) => String(l.id) === lessonId)
  const lesson = index >= 0 ? lessons[index] : null
  if (!lesson) {
    return (
      <PublicShell>
        <div className="py-16 text-center" data-testid="lesson-missing">
          <h1 className="text-xl font-semibold text-gray-100">This lesson could not be found</h1>
          <Link to={coursePath(course.slug)} className="mt-3 inline-block text-sm text-sky-300 underline">
            Back to {course.title}
          </Link>
        </div>
      </PublicShell>
    )
  }

  const prev = index > 0 ? lessons[index - 1] : null
  const next = index < lessons.length - 1 ? lessons[index + 1] : null
  const locked = lesson.content === null && lesson.video_url === null && !enrollment && !manages
  const embed = videoEmbedUrl(lesson.video_url)
  // Only a web link becomes a link: never javascript: or data: (the server
  // refuses them now, but a row saved before that could hold one).
  const videoLink = lesson.video_url && /^https?:\/\//i.test(lesson.video_url) ? lesson.video_url : null
  const done = completed.has(lesson.id)
  const isQuiz = lesson.lesson_type === 'quiz'

  const markDone = async () => {
    if (!enrollment) return
    setMarking(true)
    setMarkError(null)
    try {
      await coursesAPI.complete(enrollment.id, lesson.id)
      setState({ ...state, completed: new Set(completed).add(lesson.id) })
    } catch (err) {
      setMarkError(apiErrorMessage(err, "Couldn't save that."))
    } finally {
      setMarking(false)
    }
  }

  return (
    <PublicShell>
      <article className="space-y-5" data-testid="lesson-page">
        <nav className="text-sm">
          <Link to={coursePath(course.slug)} className="text-sky-300 hover:underline">
            ← {course.title}
          </Link>
        </nav>
        <header>
          <p className="text-xs uppercase tracking-wider text-gray-500">
            Lesson {index + 1} of {lessons.length}
          </p>
          <h1 className="mt-1 text-2xl font-bold text-gray-50">{lesson.title}</h1>
          {lesson.description && <p className="mt-1 text-gray-300">{lesson.description}</p>}
        </header>

        {locked ? (
          <div
            className="rounded-xl border border-terminal-border bg-terminal-surface p-6 text-sm text-gray-300"
            data-testid="lesson-locked"
          >
            This lesson is for the course&rsquo;s students.{' '}
            <Link to={coursePath(course.slug)} className="font-medium text-sky-300 underline">
              See how to join
            </Link>
          </div>
        ) : (
          <>
            {(embed || videoLink) &&
              (embed ? (
                <div className="aspect-video overflow-hidden rounded-xl border border-terminal-border">
                  <iframe
                    src={embed}
                    title={lesson.title}
                    className="h-full w-full"
                    allow="encrypted-media; picture-in-picture; fullscreen"
                    referrerPolicy="strict-origin-when-cross-origin"
                    data-testid="lesson-video"
                  />
                </div>
              ) : (
                <a
                  href={videoLink ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-sm font-medium text-sky-300 underline"
                  data-testid="lesson-video-link"
                >
                  Watch the video
                </a>
              ))}
            {isQuiz && lesson.quiz_id && (
              <QuizPanel
                key={lesson.quiz_id}
                quizId={lesson.quiz_id}
                onPassed={() => setState({ ...state, completed: new Set(completed).add(lesson.id) })}
              />
            )}
            {lesson.content && (
              <div
                className={LESSON_TEXT}
                data-testid="lesson-content"
                dangerouslySetInnerHTML={{ __html: lessonHtml(lesson.content) }}
              />
            )}
          </>
        )}

        <div className="flex flex-wrap items-center gap-3 border-t border-terminal-border pt-4">
          {enrollment &&
            !locked &&
            (done ? (
              <span className="text-sm font-medium text-success-500" data-testid="lesson-done">
                ✓ Done
              </span>
            ) : isQuiz ? (
              <span className="text-sm text-gray-400">Pass the quiz to finish this lesson.</span>
            ) : (
              <button
                type="button"
                onClick={markDone}
                disabled={marking}
                className="rounded-md border border-terminal-border px-3 py-1.5 text-sm text-gray-100 hover:bg-white/5 disabled:opacity-50"
              >
                {marking ? 'Saving…' : 'Mark as done'}
              </button>
            ))}
          {markError && (
            <span role="alert" className="text-sm text-amber-300">
              {markError}
            </span>
          )}
          <span className="ml-auto flex gap-3 text-sm">
            {prev && (
              <Link to={lessonPath(course.slug, prev.id)} className="text-sky-300 hover:underline">
                ← {prev.title}
              </Link>
            )}
            {next && (
              <Link to={lessonPath(course.slug, next.id)} className="text-sky-300 hover:underline" data-testid="next-lesson">
                {next.title} →
              </Link>
            )}
          </span>
        </div>

        {(enrollment || manages) && !locked && !isQuiz && <LessonTutor key={lesson.id} lesson={lesson} />}

        <p className="text-xs text-gray-500">{COURSE_DISCLAIMER}</p>
      </article>
    </PublicShell>
  )
}

export default LessonPage
